import React, { FC, useEffect, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { type CardDispute } from '@column/api';
import { Button, Icon } from '@column/column-ui-kit';
import { Badge } from '@column/ui';

import { CustomPageContext, PageTabs } from '~/app/Layout';
import { ROUTE } from '~/app/routes';
import { InlineCopyInput, Loading, PageHeader } from '~/components';
import { useCardDispute } from '~/lib/api/cardDisputes';
import { getDisputeReasonLabel, getDisputeStatusBadgeClasses, getDisputeStatusLabel } from '~/lib/disputes/labels';
import { useNavigate } from '~/lib/navigation';
import { useNotificationStore } from '~/stores/Notification';
import { Flex, Inner, Line, Text } from '~/styles';

import { IssuerDashboardGate } from './IssuerDashboardGate';

interface Params {
  id: string;
}

export interface CardDisputeProps {
  cardDispute: CardDispute;
}

const HeaderRow = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const InlineCopyInputWrapper = styled(Flex)`
  width: 100%;
  gap: 8px;
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    width: auto;
  }
`;

export const PageCardDispute: FC<CustomPageContext> = ({ entries }) => (
  <IssuerDashboardGate>
    <PageCardDisputeContent entries={entries} />
  </IssuerDashboardGate>
);

const PageCardDisputeContent: FC<CustomPageContext> = ({ entries }) => {
  const navigate = useNavigate();
  const { addDangerNotification } = useNotificationStore();
  const { id } = useParams<keyof Params>() as Params;

  const {
    data: cardDispute,
    isLoading,
    error: cardDisputeError,
    refetch,
    isFetching,
  } = useCardDispute({ disputeId: id });

  useEffect(() => {
    if (cardDisputeError) {
      navigate(ROUTE.CARD_DISPUTES);
      addDangerNotification({
        content: (cardDisputeError as any).message || 'Failed to load card dispute',
      });
    }
  }, [cardDisputeError, navigate, addDangerNotification]);

  const outletContext = useMemo(() => (cardDispute ? { cardDispute } : undefined), [cardDispute]);

  return (
    <Loading isLoading={isLoading} fullHeight>
      <PageHeader
        text={cardDispute ? `Dispute · ${getDisputeReasonLabel(cardDispute.reason)}` : 'Card Disputes'}
        backPath={ROUTE.CARD_DISPUTES}
        tabs={<PageTabs entries={entries} />}
        backButton
      />
      {id && cardDispute && (
        <>
          <Inner pt={12} px="var(--page-margin)" pb={16}>
            <Flex flexDirection="column" gap={12}>
              <HeaderRow>
                {/* The status chip lives here, not in PageHeader's decoration: PageTitle styles every
                    descendant span with ellipsis + max-width, which clips Badge chips. */}
                <Flex alignItems="center" gap={8}>
                  <Text as="h2" size={16} letterSpacing="tight" weight="medium" color="foreground">
                    Dispute · {getDisputeReasonLabel(cardDispute.reason)}
                  </Text>
                  <Badge variant="outline" className={getDisputeStatusBadgeClasses(cardDispute.status)}>
                    {getDisputeStatusLabel(cardDispute.status)}
                  </Badge>
                </Flex>

                <InlineCopyInputWrapper>
                  <InlineCopyInput label="Card Dispute ID" value={cardDispute.id} minWidth="300px" isMonospaced />
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => refetch()}
                    icon={<Icon.Reset />}
                    isDisabled={isFetching}
                  >
                    Refresh
                  </Button>
                </InlineCopyInputWrapper>
              </HeaderRow>
            </Flex>
          </Inner>
          <Line />
        </>
      )}
      {outletContext && <Outlet context={outletContext} />}
    </Loading>
  );
};
